/**
 * DriftBar — HUD element for the drift (hold-to-slide) meter.
 *
 * A thin amber bar under the health bar: it fills as the meter refills, dims
 * while it is too low to start a slide, and brightens while sliding. The meter
 * itself is server-authoritative and client-predicted (shared/Movement.js);
 * this module only draws it.
 *
 * create(hud) builds the PIXI objects into hud.elements.driftBar,
 * position(hud) lays them out relative to the health bar, update(hud, …)
 * redraws from game state. `hud` is the PixiHUDRenderer coordinator.
 */
import * as PIXI from 'pixi.js';
import { GameState } from '../../../core/GameState.js';
import { changed, invalidate } from './dirty.js';

/** Drift meter: amber when ready, hot while sliding, dim when too low to start. */
export const DRIFT_COLORS = { ready: 0xffb020, sliding: 0xffe08a, low: 0x8a6a2a };

const BAR_HEIGHT = 10;
const GAP_BELOW_HEALTH = 6;

export function create(hud) {
  const container = new PIXI.Container();
  hud.hudContainer.addChild(container);

  const border = new PIXI.Graphics();
  container.addChild(border);

  const background = new PIXI.Graphics();
  container.addChild(background);

  // Fill is a Sprite, not Graphics: resizing a sprite avoids re-tessellating
  // geometry every frame while the meter drains (same trick as HealthBar).
  const fill = new PIXI.Sprite(PIXI.Texture.WHITE);
  fill.anchor.set(0, 0);
  fill.tint = DRIFT_COLORS.ready;
  container.addChild(fill);

  const label = new PIXI.Text({
    text: 'DRIFT',
    style: { fontFamily: 'Arial', fontSize: 11, fontWeight: 'bold', fill: 0xffffff, letterSpacing: 1 }
  });
  label.anchor.set(1, 0.5);
  label.alpha = 0.8;
  container.addChild(label);

  hud.elements.driftBar = { container, border, background, fill, label };
  hud._positionDriftBar();
}

export function position(hud) {
  const hb = hud.elements.healthBar;
  const db = hud.elements.driftBar;
  if (!hb || !db || hb.barX === undefined) return;

  const barX = hb.barX;
  const barY = hb.barY + hb.barHeight + GAP_BELOW_HEALTH;
  const barWidth = hb.barWidth;

  db.border.clear();
  db.border.setStrokeStyle({ width: 1, color: 0xffffff, alpha: 0.7 });
  db.border.rect(barX, barY, barWidth, BAR_HEIGHT);
  db.border.stroke();

  db.background.clear();
  db.background.rect(barX + 1, barY + 1, barWidth - 2, BAR_HEIGHT - 2);
  db.background.fill({ color: 0x1a1408 });

  db.fill.x = barX + 1;
  db.fill.y = barY + 1;
  db.fill.width = barWidth - 2;
  db.fill.height = BAR_HEIGHT - 2;

  db.barX = barX;
  db.barY = barY;
  db.barWidth = barWidth;
  db.barHeight = BAR_HEIGHT;
  db.fillMaxWidth = barWidth - 2;

  db.label.x = barX - 8;
  db.label.y = barY + BAR_HEIGHT / 2;

  // A resize invalidates the cached fill width, so force the next update to redraw.
  invalidate(db);
}

/**
 * @param {Object} hud - the PixiHUDRenderer coordinator
 * @param {Object} localPlayer - local player data (localPlayer.drift is the predicted meter)
 */
export function update(hud, localPlayer) {
  const db = hud.elements.driftBar;
  if (!db || db.fillMaxWidth === undefined) return;

  const params = GameState.DRIFT;
  const d = localPlayer?.drift;
  const max = params?.maxMeter || 100;
  const min = params?.minMeterToStart || 0;
  const meter = d ? Math.max(0, Math.min(max, d.meter)) : max;
  const active = !!d?.active;

  // pct is 0-200 so the dirty check still sees a half-percent of drain.
  const vm = { pct: Math.round((meter / max) * 200), active, low: meter < min };
  if (!changed(db, 'fill', vm)) return;

  db.fill.width = db.fillMaxWidth * (vm.pct / 200);
  db.fill.tint = active ? DRIFT_COLORS.sliding : vm.low ? DRIFT_COLORS.low : DRIFT_COLORS.ready;
  db.label.alpha = active ? 1 : 0.8;
}
